import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';
import { type SchemaDigitalOrder } from '@/shared/api/openApi';
import type { DeliveryChunk, OrderDeliveryMethod, OrderDto } from '@/shared/api/order';
import { OrderDeliveryMethods, OrderDeliveryServices, type ServiceType } from '@/shared/api/order';
import { intervalField, type IntervalFieldValue } from '@/shared/components/IntervalInput';
import { toUnixTimestamp } from '@/shared/lib/datetime';
import { getDefaultPaymentMethod } from '@/shared/lib/getDefaultPaymentMethod';
import { type DigitalOrderFormValues } from '../../model/forms/digitalOrderForm';
import { fieldClient } from '../../model/forms/fieldClient';
import { fieldCourierAddress } from '../../model/forms/fieldCourierAddress';
import { fieldPickupAddress } from '../../model/forms/fieldPickupAddress';
import { fieldSelfPickUpAddress } from '../../model/forms/fieldSelfPickUpAddress';
import type { OrderFormValues } from '../../model/forms/orderForm';

const isFilled = <T extends object | undefined>(target: T): target is NonNullable<T> =>
    Boolean(target) && Object.keys(pickBy(target, identity)).length > 0;

function buildOrderClient(order: OrderDto | SchemaDigitalOrder): OrderFormValues['client'] {
    return Object.assign(
        {},
        fieldClient.defaultValue,
        pickBy(
            isFilled(order.client)
                ? ({
                      firstName: order.client.first_name,
                      lastName: order.client.last_name,
                      email: order.client.email,
                      phone: order.client.phone,
                  } satisfies Partial<OrderFormValues['client']>)
                : {},
            identity
        )
    );
}

function getDeliveryServiceType(
    deliveryChunkInfo: DeliveryChunk['delivery_info'],
    method: OrderDeliveryMethod
): ServiceType {
    switch (method) {
        case OrderDeliveryMethods.COURIER:
            return deliveryChunkInfo.courier_delivery_service_type;
        case OrderDeliveryMethods.PICKUP_POINT:
            return deliveryChunkInfo.pickup_point_delivery_service_type as ServiceType;

        default:
            return OrderDeliveryServices.YANDEX_DELIVERY;
    }
}

function buildChunkDeliveryInterval(order: OrderDto, method: OrderDeliveryMethod, chunkId: number): IntervalFieldValue {
    let interval = null;

    const chunk = order.delivery_chunks.find((item) => item.id === chunkId);

    if (method === OrderDeliveryMethods.COURIER) {
        interval = chunk?.delivery_info.courier_interval;
    }

    if (method === OrderDeliveryMethods.PICKUP_POINT) {
        interval = chunk?.delivery_info.pickup_point_interval;
    }

    if (method === OrderDeliveryMethods.SELF_PICK_UP) {
        interval = chunk?.delivery_info.self_pick_up_interval;
    }

    const intervalFrom = toUnixTimestamp(interval?.from);
    const intervalTo = toUnixTimestamp(interval?.to);

    if (intervalFrom && intervalTo && chunk) {
        return {
            type: 'interval',
            from: intervalFrom,
            to: intervalTo,
            serviceType: getDeliveryServiceType(chunk.delivery_info, method),
            price: null,
            currency: null,
        };
    }

    return intervalField.defaultValue;
}

function buildPickupAddress(order: OrderDto, locality: string): OrderFormValues['addressPickup'] {
    return Object.assign(
        {},
        fieldPickupAddress.defaultValue,
        pickBy(
            {
                pickupPointId: order.delivery.address?.pickup_point_id ?? '',
                address: order.delivery.address?.pickup_point_address ?? '',
                locality: order.delivery.address?.pickup_point_locality ?? locality,
                serviceType: order.delivery.pickup_point_delivery_service_type ?? null,
            },
            identity
        )
    );
}

function buildSelfPickupAddress(order: OrderDto, locality: string): OrderFormValues['addressSelfPickUp'] {
    return Object.assign(
        {},
        fieldSelfPickUpAddress.defaultValue,
        pickBy(
            {
                pickUpWarehouseId: order.delivery.address?.self_pick_up_warehouse_id ?? '',
                address: order.delivery.address?.self_pick_up_address ?? '',
                locality: order.delivery.address?.self_pick_up_locality ?? locality,
            },
            identity
        )
    );
}

function buildCourierAddress(order: OrderDto, locality: string): OrderFormValues['addressCourier'] {
    return Object.assign(
        {},
        fieldCourierAddress.defaultValue,
        pickBy(
            {
                locality: order.delivery.address?.courier_locality ?? locality,
                address: order.delivery.address?.courier_address ?? '',
                appartment: order.delivery.address?.appartment ?? '',
                entrance: order.delivery.address?.entrance ?? '',
                floor: order.delivery.address?.floor ?? '',
                intercom: order.delivery.address?.intercom ?? '',
                comment: order.delivery?.delivery_notes ?? '',
            },
            identity
        )
    );
}

export const toFormValues = (order: OrderDto): OrderFormValues => {
    const locality = order.delivery.address.locality || 'Москва';
    const addressCourier = buildCourierAddress(order, locality);
    const addressPickup = buildPickupAddress(order, locality);
    const addressSelfPickUp = buildSelfPickupAddress(order, locality);
    const defaultDeliveryMethod = addressPickup ? OrderDeliveryMethods.PICKUP_POINT : null;

    const result = {
        locality,
        addressCourier,
        client: buildOrderClient(order),
        addressPickup: addressPickup,
        addressSelfPickUp: addressSelfPickUp,
        promocode: '',
        promocodeApply: order.promocode?.code ?? '',
        giftCardApply: order.gift_card?.code ?? '',
        paymentMethod: order.payment.method ?? getDefaultPaymentMethod(),
        deliveryMethod: order.delivery.method || defaultDeliveryMethod,
        deliveryCourierInterval: order.delivery_chunks.map((chunk) => {
            return {
                chunkId: chunk.id,
                intervalField: buildChunkDeliveryInterval(order, OrderDeliveryMethods.COURIER, chunk.id),
            };
        }),
        deliveryPickupPointInterval: order.delivery_chunks.map((chunk) => {
            return {
                chunkId: chunk.id,
                intervalField: buildChunkDeliveryInterval(order, OrderDeliveryMethods.PICKUP_POINT, chunk.id),
            };
        }),
        deliverySelfPickUpInterval: order.delivery_chunks.map((chunk) => {
            return {
                chunkId: chunk.id,
                intervalField: buildChunkDeliveryInterval(order, OrderDeliveryMethods.SELF_PICK_UP, chunk.id),
            };
        }),
        bonusesApplied: Boolean(
            order.loyalty_v2?.is_registered_user && order.loyalty_v2.registered_user_loyalty.user_agreed_to_charge
        ),
    };

    return result;
};

export const toDigitalFormValues = (order: SchemaDigitalOrder): DigitalOrderFormValues | undefined => {
    return {
        client: buildOrderClient(order),
    };
};
